/**
 * LLM Provider Configuration page — tenant-scoped API keys and connection settings for AI model
 * providers.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` for the title and primary action, `CordioAbpPagedDataTable` for the grid, and a
 * `.llm-provider-config` BEM block. The endpoint returns a bare `LlmProviderConfigDto[]` with no
 * paging or search, so the `dataSource` fetches the whole list and does the search + page slice
 * locally before handing `createCortexPagedDataSource` a `PagedResult`-shaped object.
 *
 * --- Column plan -------------------------------------------------------------------------------
 * field           | header     | width | cell type              | filter
 * ----------------|------------|-------|------------------------|----------------------------
 * providerName    | Provider   | 20%   | text, clickable        | toolbar search (local)
 * name            | Name       | 16%   | text                   | toolbar search (local)
 * baseUrl         | Base URL   | 20%   | text, 50 + readMore    | —
 * deploymentName  | Deployment | 14%   | text                   | —
 * hasApiKey       | API Key    | 14%   | custom (Chip)          | —
 * isActive        | Status     | 16%   | custom (Chip)          | status tabs → `isActive`
 * -----------------------------------------------------------------------------------------------
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import {
  InlineErrorPanel,
  LoadingSkeleton,
  PageContainer,
} from '@coordio/ui/layout';
import { Plus } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { llmService } from '../services/llmService';
import { useNotificationStore } from '../store/notificationStore';
import { useConfirmDialog } from '../components/common/ConfirmDialog';
import { ProviderConfigForm } from '../components/llm/ProviderConfigForm';
import type { LlmProviderConfigDto, LlmProviderDto } from '../types/llm';
import type { PagedResult } from '../types/tenant';

import './LlmProviderConfigPage.scss';

/**
 * Quick-filter tabs over `isActive`.
 *
 * The All tab declares the literal `'all'` because the grid builds each tab's test id as
 * `cordio-datatable-status-tab-<value>`; an empty value emits `cordio-datatable-status-tab-` and
 * GRID-13's `…-status-tab-all` selector matches nothing.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/** Free-text match over the two identifying fields. */
function matchesSearch(config: LlmProviderConfigDto, term: string): boolean {
  if (!term) return true;
  return `${config.providerName} ${config.name ?? ''}`.toLowerCase().includes(term);
}

/**
 * Wraps an unpaged array response as the one page the grid asked for.
 *
 * `totalCount` is exact — the endpoint returns the whole set.
 */
function toClientPage<T>(items: readonly T[], pageNumber: number, pageSize: number): PagedResult<T> {
  const start = Math.max(0, (pageNumber - 1) * pageSize);
  return {
    items: items.slice(start, start + pageSize) as T[],
    totalCount: items.length,
    pageNumber,
    pageSize,
    totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
    hasPreviousPage: pageNumber > 1,
    hasNextPage: start + pageSize < items.length,
  };
}

export const LlmProviderConfigPage = () => {
  const [providers, setProviders] = useState<LlmProviderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LlmProviderConfigDto | null>(null);
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  /** Provider catalog, loaded once for the form. */
  const [allConfigs, setAllConfigs] = useState<LlmProviderConfigDto[]>([]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    llmService.getProviders(true).then(setProviders).catch(() => {});
    llmService
      .getProviderConfigs()
      .then((data) => {
        setAllConfigs(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          (err instanceof Error ? err.message : 'Failed to load provider configurations');
        setError(message);
        addNotification(message, 'error');
        setLoading(false);
      });
  }, [addNotification, listVersion]);

  const configuredProviderIds = useMemo(
    () => new Set(allConfigs.map((c) => c.providerId)),
    [allConfigs],
  );
  const availableProviders = useMemo(
    () => providers.filter((p) => !configuredProviderIds.has(p.id)),
    [providers, configuredProviderIds],
  );

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<LlmProviderConfigDto>(async (query: CortexPagedQuery) => {
        const data = await llmService.getProviderConfigs();
        setAllConfigs(data);

        const term = typeof query.searchTerm === 'string' ? query.searchTerm.trim().toLowerCase() : '';
        const status = query[STATUS_TAB_PARAM_KEY];

        let filtered = data.filter((config) => matchesSearch(config, term));

        if (status === 'true') filtered = filtered.filter((c) => c.isActive);
        if (status === 'false') filtered = filtered.filter((c) => !c.isActive);

        return toClientPage(filtered, query.pageNumber, query.pageSize);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<LlmProviderConfigDto>>>(
    () => [
      { field: 'providerName', header: 'Provider', type: 'text', width: '20%', clickable: true },
      {
        field: 'name',
        header: 'Name',
        type: 'text',
        width: '16%',
        valueGetter: (row) => row.name || '—',
      },
      {
        field: 'baseUrl',
        header: 'Base URL',
        type: 'text',
        width: '20%',
        maxLength: 50,
        showReadMore: true,
        valueGetter: (row) => row.baseUrl || '—',
      },
      {
        field: 'deploymentName',
        header: 'Deployment',
        type: 'text',
        width: '14%',
        valueGetter: (row) => row.deploymentName || '—',
      },
      {
        field: 'hasApiKey',
        header: 'API Key',
        type: 'custom',
        width: '14%',
        customCell: (row) =>
          row.hasApiKey ? (
            <Chip
              label={row.apiKeyLast5 ? `…${row.apiKeyLast5}` : 'Configured'}
              color="green"
              variant="outlined"
            />
          ) : (
            <Chip label="Not Set" color="amber" variant="outlined" />
          ),
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '16%',
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search providers by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Provider config status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
    }),
    [],
  );

  const handleAdd = useCallback(() => {
    setEditingConfig(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((config: LlmProviderConfigDto) => {
    setEditingConfig(config);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingConfig(null);
  }, []);

  const handleFormSaved = useCallback(() => {
    setFormOpen(false);
    setEditingConfig(null);
    setListVersion((v) => v + 1);
  }, []);

  const handleDeleteClick = useCallback(
    async (config: LlmProviderConfigDto) => {
      const ok = await confirm({
        title: 'Delete Provider Configuration',
        message: `Are you sure you want to delete the configuration for ${config.providerName}? This will remove the API key and connection settings. Agents using models from this provider will no longer work.`,
        confirmText: 'Delete',
      });
      if (!ok) return;

      try {
        await llmService.deleteProviderConfig(config.id);
        addNotification(`${config.providerName} configuration deleted`, 'success');
        setListVersion((v) => v + 1);
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to delete configuration';
        addNotification(message, 'error');
      }
    },
    [confirm, addNotification],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<LlmProviderConfigDto>>>(
    () => [
      {
        icon: 'square-pen',
        tooltip: 'Edit configuration',
        onClick: (row) => handleEdit(row),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete configuration',
        className: 'llm-provider-config__action--destructive',
        onClick: (row) => void handleDeleteClick(row),
      },
    ],
    [handleEdit, handleDeleteClick],
  );

  if (loading) {
    return (
      <div className="llm-provider-config" data-testid="llm-provider-form-page">
        <ShellHeader
          title="LLM Provider Configurations"
          description="Configure API keys and connection settings for AI model providers. Models from configured providers will be available when creating agents."
          data-testid="llm-provider-header"
        />
        <PageContainer data-testid="llm-provider-page-container">
          <LoadingSkeleton rows={6} data-testid="llm-provider-loading" />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="llm-provider-config" data-testid="llm-provider-form-page">
      <ShellHeader
        title="LLM Provider Configurations"
        description="Configure API keys and connection settings for AI model providers. Models from configured providers will be available when creating agents."
        data-testid="llm-provider-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            onClick={handleAdd}
            disabled={availableProviders.length === 0}
            data-testid="llm-provider-add-btn"
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Add Provider
          </Button>
        }
      />

      {error && (
        <div className="llm-provider-config__error">
          <InlineErrorPanel
            message={error}
            data-testid="llm-provider-error-panel"
            action={
              <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)} data-testid="dismiss-llm-provider-btn">
                Dismiss
              </Button>
            }
          />
        </div>
      )}

      {availableProviders.length === 0 && allConfigs.length > 0 && (
        <p className="llm-provider-config__notice">
          All available providers have been configured. You can edit existing configurations below.
        </p>
      )}

      <CordioAbpPagedDataTable<LlmProviderConfigDto>
        className="llm-provider-config__table"
        data-testid="llm-provider-table"
        columns={columns}
        dataSource={dataSource}
        initialPageSize={25}
        maxBodyHeight="520px"
        listToolbar={listToolbar}
        actions={actions}
        onRowClick={(row) => handleEdit(row)}
      />

      <ProviderConfigForm
        open={formOpen}
        onClose={handleFormClose}
        onSaved={handleFormSaved}
        config={editingConfig}
        providers={providers}
        configuredProviderIds={configuredProviderIds}
      />
    </div>
  );
};
